import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';

function RevealSection({ children, className = '', delay = 0 }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const LAST_UPDATED = 'July 2026';

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="px-6 lg:px-8 pt-[80px] pb-[100px] md:pt-[120px] md:pb-[120px]">
      <div className="max-w-2xl mx-auto">
        <RevealSection>
          <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-4">Legal</p>
          <h1 className="font-inter font-bold text-[38px] md:text-[54px] leading-[1.1] tracking-tight text-km-white">
            Privacy Policy
          </h1>
          <p className="mt-6 font-inter font-medium text-sm text-km-gray-5">Last updated: {LAST_UPDATED}</p>
        </RevealSection>

        <RevealSection delay={100}>
          <div className="mt-10 p-[30px] md:p-[50px] rounded-[10px] bg-km-card-gradient border border-km-gray-3 space-y-8 font-inter font-medium text-base text-km-gray-5 leading-[1.5]">

            <section className="space-y-4">
              <p>
                King Makers ("we," "us," or "our") operates the website kingmakersinc.ca and the products and services offered through it. We are a sole proprietorship registered in Ontario, Canada, based in Mississauga, ON. This policy explains what personal information we collect, why, how we handle it, and the choices you have. We take a deliberately minimal approach: we collect only what we need to respond to you and deliver what you ask for.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Information we collect</h2>
              <p>
                We collect personal information only when you choose to give it to us by filling out a form or booking a call. Specifically:
              </p>
              <ul className="space-y-3 pl-1">
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Contact form:</strong> your name, email address, subject, and message.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Newsletter signup:</strong> your email address.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Product notification signup:</strong> your email address and the product you asked to be notified about.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Discovery call booking:</strong> when you book a call through our scheduling provider, your name, email address, and your answers to the booking questions.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Purchases:</strong> when you buy a product, payment is processed by our payment provider. We do not collect or store your card details; we receive confirmation of purchase and the email associated with it.</span></li>
              </ul>
              <p>
                Each form submission is stored with a system-generated identifier and the date and time it was received. We do not collect your IP address, device, or browsing behavior through our own code.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Cookies and tracking</h2>
              <p>
                Our website does not set cookies of its own and does not run analytics or tracking scripts. We do not track your activity across pages or across other websites.
              </p>
              <p>
                Our site loads fonts from Google Fonts, which means Google receives your IP address when a page loads, as it does for a large share of websites. Pages that link out to our payment, scheduling, and delivery providers (see below) are governed by those providers' own privacy policies once you follow the link.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">How we use your information</h2>
              <p>We use the information you give us to:</p>
              <ul className="space-y-3 pl-1">
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span>Respond to your inquiries and provide the products or services you request.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span>Send you the confirmation and delivery details for a purchase.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span>Send newsletter or product-launch updates, if you asked to receive them. You can unsubscribe at any time.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span>Schedule and prepare for a call you booked with us.</span></li>
              </ul>
              <p>
                We do not sell your personal information, and we do not share it for advertising. We only use it for the purposes you provided it.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Service providers we rely on</h2>
              <p>
                We use a small set of trusted third-party services to run the business. Each receives only the information needed for its function, and each has its own privacy policy:
              </p>
              <ul className="space-y-3 pl-1">
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">MongoDB Atlas:</strong> securely stores form submissions.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Resend:</strong> sends email notifications and replies related to your submission.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Stripe:</strong> processes payments. Your payment details are entered on Stripe's secure checkout, not on our site.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Calendly:</strong> handles discovery-call booking and collects the details you enter when scheduling.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Notion:</strong> hosts the digital product you access after purchase.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Vercel and Render:</strong> host our website and its backend.</span></li>
                <li className="flex gap-3"><span className="text-km-gold">&bull;</span><span><strong className="font-semibold text-km-white">Google Fonts:</strong> serves the fonts used on the site.</span></li>
              </ul>
              <p>
                Some of these providers are located in the United States or other countries, which means your information may be processed outside Canada and be subject to the laws of those jurisdictions.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">How long we keep it</h2>
              <p>
                We keep the information you submit for as long as needed to respond to you, deliver what you requested, and meet our record-keeping and legal obligations. You can ask us to delete your information at any time, and we will do so unless we are required to keep it.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Your rights</h2>
              <p>
                Under Canadian privacy law, including PIPEDA and Quebec's Law 25, you have the right to access the personal information we hold about you, ask us to correct it, ask us to delete it, and withdraw your consent to our use of it. To exercise any of these rights, email us at the address below and we will respond within a reasonable time.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Children</h2>
              <p>
                Our products and services are intended for business professionals and are not directed at children. We do not knowingly collect personal information from anyone under the age of majority in their province or territory.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-inter font-semibold text-[20px] md:text-[24px] leading-[1.2] text-km-white">Changes to this policy</h2>
              <p>
                We may update this policy as our business or the law changes. When we do, we will update the date at the top of this page. Continued use of the site after a change means you accept the updated policy.
              </p>
            </section>

            <div className="pt-8 border-t border-km-gray-3">
              <p className="font-inter font-medium text-km-white">Contact</p>
              <p className="mt-2">
                Questions about this policy or your personal information? Email{' '}
                <a href="mailto:hello@kingmakersinc.ca" className="text-km-gold hover:text-km-gold-deep transition-colors cursor-pointer">hello@kingmakersinc.ca</a>.
              </p>
              <p className="mt-2 text-sm text-km-gray-5">King Makers, Mississauga, Ontario, Canada.</p>
            </div>

          </div>
        </RevealSection>

        <RevealSection delay={200}>
          <div className="mt-16 text-center">
            <Link to="/" className="font-inter font-medium text-sm text-km-gray-5 hover:text-km-white transition-colors cursor-pointer">
              &larr; Back to King Makers
            </Link>
          </div>
        </RevealSection>
      </div>
    </div>
  );
}
